import { h } from 'preact';
import { Text } from 'preact-i18n';

import { SCENE_PALETTE, FactoryWall } from './progressSceneParts';

// Progress scene shown while «Opprett alle samlefakturaer» runs (1–5 minutes).
// Driven by the real loop in allVisitsTable: one customer at a time, each with
// N visits in status «Samlefaktura» (1003). Those visit cards on the left fly
// onto the invoice sheet in the middle, which gets stamped «FAKTURA» (the
// resulting sale) and then idles until the loop moves on. The
// <svg> is keyed on `current`, so every customer remounts the scene: the
// previous (stamped) sheet lands on the pile of finished invoices on the right
// while the new sheet drops in. Tying the hand-off to the real step means the
// desk never sits empty, however long a customer takes.
//
// Styles live in a literal <style> inside the <svg> (same trick as the
// pull-to-refresh scenes) so preact-cli's CSS Modules hashing can't touch the
// class names.

export const MAX_VISIBLE_CARDS = 8;
export const MAX_VISIBLE_PILE = 10;

// Seconds between two cards leaving the stack: 0.3s, compressed so the whole
// fly-in never takes more than ~2s however many visits a customer has.
export function cardStepSeconds(saleCount) {
    if (!(saleCount > 0)) {
        return 0.3;
    }
    return Math.min(0.3, 2 / saleCount);
}

const SCENE_CSS = `
.ci-scene .ci-anim {
    transform-box: fill-box;
    transform-origin: center;
}
@keyframes ciBelt {
    from { transform: translateX(0); }
    to   { transform: translateX(40px); }
}
@keyframes ciCardFly {
    0%   { transform: translate(0, 0) scale(1) rotate(0deg); opacity: 1; }
    70%  { opacity: 1; }
    100% { transform: translate(262px, -52px) scale(0.45) rotate(8deg); opacity: 0; }
}
@keyframes ciLineIn {
    from { opacity: 0; transform: translateX(-12px); }
    to   { opacity: 1; transform: translateX(0); }
}
@keyframes ciSheetIn {
    from { opacity: 0; transform: translateY(-28px); }
    to   { opacity: 1; transform: translateY(0); }
}
@keyframes ciSheetIdle {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-4px); }
}
@keyframes ciStampPop {
    0%   { opacity: 0; transform: scale(2.6); }
    60%  { opacity: 1; transform: scale(0.88); }
    100% { opacity: 1; transform: scale(1); }
}
@keyframes ciPileLand {
    0%   { opacity: 0; transform: translate(-220px, -40px) scale(1.6); }
    30%  { opacity: 1; }
    100% { opacity: 1; transform: translate(0, 0) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
    .ci-scene .ci-anim { animation: none !important; }
    .ci-scene .ci-line, .ci-scene .ci-stamp, .ci-scene .ci-pile-land, .ci-scene .ci-sheet { opacity: 1 !important; }
}
`;

// One visit card in the stack on the left: paw dot + two text lines.
const VisitCard = ({ x, y, delay, p }) => (
    <g
        class='ci-card ci-anim'
        style={`animation: ciCardFly 0.55s ease-in ${delay.toFixed(2)}s forwards;`}
    >
        <rect x={x} y={y} width='110' height='40' rx='6' fill={p.card} stroke={p.cardStroke} stroke-width='1.5' />
        <circle cx={x + 18} cy={y + 20} r='9' fill={p.paw} opacity='0.9' />
        <circle cx={x + 14} cy={y + 15} r='2.2' fill={p.card} />
        <circle cx={x + 22} cy={y + 15} r='2.2' fill={p.card} />
        <ellipse cx={x + 18} cy={y + 23} rx='4' ry='3' fill={p.card} />
        <rect x={x + 34} y={y + 12} width='58' height='5' rx='2.5' fill={p.line} />
        <rect x={x + 34} y={y + 23} width='40' height='5' rx='2.5' fill={p.line} />
    </g>
);

const PileSheet = ({ x, y, p, className, style }) => (
    <g class={className} style={style}>
        <rect x={x + 2} y={y + 2} width='70' height='90' rx='3' fill={p.sheetShadow} />
        <rect x={x} y={y} width='70' height='90' rx='3' fill={p.sheet} />
        <rect x={x + 10} y={y + 14} width='36' height='4' rx='2' fill={p.line} />
        <rect x={x + 10} y={y + 26} width='50' height='3' rx='1.5' fill={p.line} />
        <rect x={x + 10} y={y + 36} width='44' height='3' rx='1.5' fill={p.line} />
        <rect x={x + 10} y={y + 46} width='50' height='3' rx='1.5' fill={p.line} />
        <circle cx={x + 50} cy={y + 72} r='10' fill='none' stroke={p.accent} stroke-width='2' opacity='0.8' />
    </g>
);

const CollectiveInvoiceProgress = ({
    current = 0, total = 0, totalSales = 0, customerName = '', saleCount = 0,
}) => {
    const p = SCENE_PALETTE;
    const visibleCards = Math.min(Math.max(saleCount, 0), MAX_VISIBLE_CARDS);
    const overflow = Math.max(saleCount - MAX_VISIBLE_CARDS, 0);
    const step = cardStepSeconds(saleCount);
    // Last card lands ~0.55s after it leaves; the stamp pops right after, then the
    // sheet idles until the loop moves to the next customer.
    const stampDelay = (visibleCards > 0 ? (visibleCards - 1) * step + 0.55 : 0.3) + 0.15;
    const idleDelay = stampDelay + 0.5;

    // Customers already finished. The latest one is the sheet landing on the pile
    // as this scene mounts; the rest sit there already.
    const done = Math.max(current - 1, 0);
    const visiblePile = Math.min(Math.max(done - 1, 0), MAX_VISIBLE_PILE);
    const pct = total > 0 ? Math.round((Math.min(current, total) / total) * 100) : 0;

    const cardIndexes = [];
    for (let i = 0; i < visibleCards; i += 1) {
        cardIndexes.push(i);
    }
    const pileIndexes = [];
    for (let j = 0; j < visiblePile; j += 1) {
        pileIndexes.push(j);
    }
    const landX = 600 + visiblePile * 2;
    const landY = 78 - visiblePile * 3;

    return (
        <div class='mt-2 rounded-3 overflow-hidden border'>
            <svg
                key={current}
                viewBox='0 0 800 230'
                preserveAspectRatio='xMidYMid meet'
                role='img'
                class='ci-scene'
                style={`display: block; width: 100%; height: auto; max-height: 260px; overflow: visible; background: ${p.backdrop};`}
            >
                <style>{SCENE_CSS}</style>
                <title>Oppretter samlefakturaer</title>

                <FactoryWall p={p} />

                {/* Conveyor belt */}
                <rect x='40' y='172' width='720' height='26' rx='13' fill={p.belt} />
                <clipPath id='ci-belt-clip'>
                    <rect x='40' y='172' width='720' height='26' rx='13' />
                </clipPath>
                <g clip-path='url(#ci-belt-clip)'>
                    <g class='ci-anim' style='animation: ciBelt 0.8s linear infinite;'>
                        {Array.from({ length: 20 }, (_, i) => (
                            <rect key={i} x={i * 40} y='172' width='14' height='26' fill={p.beltStripe} opacity='0.7' />
                        ))}
                    </g>
                </g>
                <circle cx='53' cy='185' r='8' fill={p.roller} />
                <circle cx='747' cy='185' r='8' fill={p.roller} />

                {/* Labels */}
                <text x='140' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={p.text}>Besøk: Samlefaktura</text>
                <text x='400' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={p.text}>Faktura</text>
                <text x='640' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={p.text}>Ferdig</text>

                {/* Visit cards — reversed so index 0 is drawn last (top of the stack) and flies first */}
                {cardIndexes.slice().reverse().map(i => (
                    <VisitCard key={i} x={85 + i * 1.5} y={128 - i * 6} delay={i * step} p={p} />
                ))}
                {overflow > 0 && (
                    <text class='ci-card-overflow' x='215' y='96' font-family='sans-serif' font-size='13' font-weight='700' fill={p.text}>+{overflow}</text>
                )}

                {/* Invoice sheet */}
                <g
                    class='ci-sheet ci-anim'
                    style={`animation: ciSheetIn 0.4s ease-out both, ciSheetIdle 2.4s ease-in-out ${idleDelay.toFixed(2)}s infinite;`}
                >
                    <rect x='333' y='34' width='140' height='138' rx='4' fill={p.sheetShadow} />
                    <rect x='330' y='30' width='140' height='138' rx='4' fill={p.sheet} />
                    <rect x='342' y='42' width='60' height='6' rx='3' fill={p.ink} opacity='0.75' />
                    <rect x='430' y='42' width='28' height='6' rx='3' fill={p.line} />
                    <line x1='342' y1='56' x2='458' y2='56' stroke={p.line} stroke-width='1' />
                    {cardIndexes.map(i => (
                        <g
                            key={i}
                            class='ci-line ci-anim'
                            style={`opacity: 0; animation: ciLineIn 0.3s ease-out ${(i * step + 0.45).toFixed(2)}s forwards;`}
                        >
                            <circle cx='347' cy={65 + i * 11} r='2.5' fill={p.paw} />
                            <rect x='354' y={62.5 + i * 11} width={62 - (i % 3) * 10} height='5' rx='2.5' fill={p.line} />
                            <rect x='436' y={62.5 + i * 11} width='22' height='5' rx='2.5' fill={p.line} />
                        </g>
                    ))}
                    <line x1='342' y1='154' x2='458' y2='154' stroke={p.line} stroke-width='1' />
                    <rect x='342' y='158' width='30' height='5' rx='2.5' fill={p.ink} opacity='0.6' />
                    <rect x='426' y='158' width='32' height='5' rx='2.5' fill={p.ink} opacity='0.6' />

                    {/* Stamp */}
                    <g transform='translate(400 112) rotate(-14)'>
                        <g
                            class='ci-stamp ci-anim'
                            style={`opacity: 0; animation: ciStampPop 0.35s ease-out ${stampDelay.toFixed(2)}s forwards;`}
                        >
                            <rect x='-56' y='-15' width='112' height='30' rx='5' fill='none' stroke={p.accent} stroke-width='3' />
                            <text x='0' y='6' text-anchor='middle' font-family='sans-serif' font-size='15' font-weight='800' letter-spacing='2' fill={p.accent}>FAKTURA</text>
                        </g>
                    </g>
                </g>

                {/* Finished pile */}
                {pileIndexes.map(j => (
                    <PileSheet key={j} className='ci-pile-sheet' x={600 + j * 2} y={78 - j * 3} p={p} />
                ))}
                {done > 0 && (
                    <PileSheet
                        className='ci-pile-land ci-anim'
                        style='opacity: 0; animation: ciPileLand 0.5s ease-in-out 0.05s forwards;'
                        x={landX}
                        y={landY}
                        p={p}
                    />
                )}
                {done > 0 && (
                    <g>
                        <circle cx='700' cy='56' r='16' fill={p.badge} />
                        <text class='ci-pile-count' x='700' y='61' text-anchor='middle' font-family='sans-serif' font-size='14' font-weight='700' fill='#ffffff'>{done}</text>
                    </g>
                )}
            </svg>

            <div class='px-3 pb-3 bg-body text-body'>
                <div class='text-center fw-bold pt-2' style='min-height: 1.5em;'>
                    {customerName}
                </div>
                <div class='progress mt-2' style='height: 10px;'>
                    <div
                        class='progress-bar bg-danger progress-bar-striped progress-bar-animated'
                        role='progressbar'
                        style={`width: ${pct}%;`}
                        aria-valuenow={current}
                        aria-valuemin='0'
                        aria-valuemax={total}
                    />
                </div>
                <div class='d-flex justify-content-between small mt-1 text-body-secondary'>
                    <span><Text id='dyrejournal.collective-invoice-current'>Current collective invoice</Text>: {current} / {total}</span>
                    <span><Text id='dyrejournal.collective-invoice-total'>Total sales for collective invoice</Text>: {totalSales}</span>
                </div>
                <div class='mt-2 p-2 bg-danger text-white rounded text-center fw-bold'>
                    <i class='fa-duotone fa-spinner fa-spin' />
                    {' '}<Text id='dyrejournal.creating-collective-invoices'>Creating all collective invoices. DO NOT CLOSE THIS WINDOW!</Text>
                </div>
            </div>
        </div>
    );
};

export default CollectiveInvoiceProgress;
